import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { toast } from 'sonner';
import { analyticsApi, ticketsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { formatRelativeTime } from '@/lib/utils';
import { 
  Ticket, 
  Users, 
  CheckCircle, 
  Clock,
  ChartLine,
  Gear,
  WarningCircle,
  Hourglass,
  ArrowRight,
  Star,
  Package
} from '@phosphor-icons/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

export const AdminDashboard = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const [overview, setOverview] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [overviewRes, chartsRes, ticketsRes] = await Promise.all([
        analyticsApi.getOverview(),
        analyticsApi.getCharts(),
        ticketsApi.list({ page_size: 5 })
      ]);
      setOverview(overviewRes.data);
      setCharts(chartsRes.data);
      setRecentTickets(ticketsRes.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) return <LoadingSpinner fullScreen />;

  const total = overview?.total_tickets || 0;
  const breached = overview?.breached_tickets || 0;
  const complianceRate = total > 0 ? Math.round(((total - breached) / total) * 100) : 100;

  const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899'];

  return (
    <DashboardLayout role="super_admin">
      <div className="page-padding max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="mb-2">
          <h1 className="text-4xl font-black tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Real-time system overview and key performance metrics</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card className="p-5 border flex items-center justify-between" data-testid="total-tickets-card">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Tickets</p>
              <p className="text-3xl font-black text-slate-800">{overview?.total_tickets || 0}</p>
              <p className="text-xs text-slate-500 font-medium">
                {overview?.resolved_tickets || 0} resolved
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg" style={{ backgroundColor: `${brandColorAccent}12` }}>
              <Ticket size={28} weight="fill" style={{ color: brandColorAccent }} />
            </div>
          </Card>

          <Card className="p-5 border flex items-center justify-between" data-testid="open-tickets-card">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Open Tickets</p>
              <p className="text-3xl font-black text-slate-800">{overview?.open_tickets || 0}</p>
              <p className="text-xs text-slate-500 font-medium">Pending resolution</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <Clock size={28} className="text-amber-500" weight="fill" />
            </div>
          </Card>

          <Card className="p-5 border flex items-center justify-between" data-testid="sla-compliance-card">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">SLA Compliance</p>
              <p className={`text-3xl font-black ${complianceRate >= 90 ? 'text-emerald-600' : complianceRate >= 70 ? 'text-amber-500' : 'text-rose-600'}`}>
                {complianceRate}%
              </p>
              <p className="text-xs text-slate-500 font-medium">
                {breached} breach{breached !== 1 ? 'es' : ''}
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <CheckCircle size={28} className={complianceRate >= 90 ? 'text-emerald-500' : 'text-amber-500'} weight="fill" />
            </div>
          </Card>

          <Card className="p-5 border flex items-center justify-between" data-testid="active-technicians-card">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Technicians</p>
              <p className="text-3xl font-black text-slate-800">{overview?.technicians_count || 0}</p>
              <p className="text-xs text-slate-500 font-medium">
                Avg rating: {overview?.average_rating || 0}/5
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Users size={28} className="text-purple-500" weight="fill" />
            </div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket Volume Chart */}
          <Card className="p-6 border lg:col-span-2 space-y-4" data-testid="ticket-volume-card">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <ChartLine size={20} style={{ color: brandColorAccent }} />
                Ticket Volume (Last 7 Days)
              </h3>
              <p className="text-xs text-slate-400">Daily created vs resolved ticket flow</p>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={charts?.volume_by_day}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brandColorAccent} stopOpacity={0.2}/>
                      <stop offset="95%" stopColor={brandColorAccent} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />
                  <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                  <Area type="monotone" name="Created" dataKey="created" stroke={brandColorAccent} strokeWidth={2.5} fillOpacity={1} fill="url(#colorCreated)" />
                  <Area type="monotone" name="Resolved" dataKey="resolved" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorResolved)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Status Distribution */}
          <Card className="p-6 border space-y-4" data-testid="status-distribution-card">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Status Distribution</h3>
              <p className="text-xs text-slate-400">Current ticket pipeline</p>
            </div>
            <div className="h-[200px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={charts?.status_distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {charts?.status_distribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs pt-1">
              {charts?.status_distribution?.map((item, idx) => (
                <div key={item.name} className="flex items-center gap-1.5 font-medium text-slate-600">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                  {item.name} ({item.value})
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Recent Tickets */}
        <Card className="border" data-testid="recent-tickets-card">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Recent Tickets</h3>
              <p className="text-xs text-slate-400">Latest incoming service requests</p>
            </div>
            <Link
              to="/ops/admin/tickets"
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-70 transition-opacity"
              style={{ color: brandColorAccent }}
            >
              View All <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          {recentTickets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/60 text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="py-3 px-5">Ticket</th>
                    <th className="py-3 px-5">Customer</th>
                    <th className="py-3 px-5">Product</th>
                    <th className="py-3 px-5">Agent</th>
                    <th className="py-3 px-5">Priority</th>
                    <th className="py-3 px-5">Status</th>
                    <th className="py-3 px-5 text-right">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-sm">
                  {recentTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-5 font-bold" style={{ color: brandColorAccent }}>
                        {ticket.ticket_number}
                      </td>
                      <td className="py-3 px-5 font-semibold text-slate-700">
                        {ticket.customer_name || '—'}
                      </td>
                      <td className="py-3 px-5">
                        <div className="font-medium text-slate-700">{ticket.product_name}</div>
                        <div className="text-[11px] text-slate-400">{ticket.issue_category}</div>
                      </td>
                      <td className="py-3 px-5 text-slate-600 text-xs font-medium">
                        {ticket.agent_name || <span className="text-slate-300">Unassigned</span>}
                      </td>
                      <td className="py-3 px-5"><PriorityBadge priority={ticket.priority} /></td>
                      <td className="py-3 px-5"><StatusBadge status={ticket.status} /></td>
                      <td className="py-3 px-5 text-right text-slate-400 text-xs">
                        {formatRelativeTime(ticket.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-10 text-center">
              <Ticket size={40} className="mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-slate-400 font-medium">No tickets yet</p>
            </div>
          )}
        </Card>

        {/* Quick Links */}
        <Card className="p-6 border" data-testid="quick-links-card">
          <h2 className="text-lg font-bold mb-4 text-slate-800">Quick Links</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Link to="/ops/admin/tickets" className="p-4 border rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-center group">
              <Ticket size={28} className="mx-auto mb-2 text-slate-400 group-hover:text-slate-600 transition-colors" />
              <p className="font-semibold text-xs text-slate-600">Manage Tickets</p>
            </Link>
            <Link to="/ops/admin/users" className="p-4 border rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-center group">
              <Users size={28} className="mx-auto mb-2 text-slate-400 group-hover:text-slate-600 transition-colors" />
              <p className="font-semibold text-xs text-slate-600">Manage Users</p>
            </Link>
            <Link to="/ops/admin/products" className="p-4 border rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-center group">
              <Package size={28} className="mx-auto mb-2 text-slate-400 group-hover:text-slate-600 transition-colors" />
              <p className="font-semibold text-xs text-slate-600">Product Catalog</p>
            </Link>
            <Link to="/ops/admin/analytics" className="p-4 border rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-center group">
              <ChartLine size={28} className="mx-auto mb-2 text-slate-400 group-hover:text-slate-600 transition-colors" />
              <p className="font-semibold text-xs text-slate-600">Analytics</p>
            </Link>
            <Link to="/ops/admin/config" className="p-4 border rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all text-center group">
              <Gear size={28} className="mx-auto mb-2 text-slate-400 group-hover:text-slate-600 transition-colors" />
              <p className="font-semibold text-xs text-slate-600">Configuration</p>
            </Link>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};